/**
 * 個別説明スケジュール枠の「予約受付締切」判定ロジック。
 *
 * 管理画面の枠分類 (受付中 / 過去) と公開フォームの候補日フィルタの両方で
 * 同一ロジックを共有するため shared に集約する。
 *
 * 日付は "YYYY-MM-DD" の純粋な暦日として扱い、UTC 正午を基準に差分を取ることで
 * 実行環境のタイムゾーン差 (ブラウザ=JST / functions=UTC) の影響を受けないようにする。
 */
import type { SettingsBriefing } from '../types/common';

type LeadDaysSettings = Pick<SettingsBriefing, 'weekdayLeadDays' | 'holidayLeadDays'>;

/** "YYYY-MM-DD" を UTC エポックミリ秒 (その日の 00:00 UTC) に変換。 */
function ymdToUtcMs(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, (m ?? 1) - 1, d ?? 1);
}

/** 枠の曜日が土日かどうか (休日扱い)。 */
export function isBriefingSlotHoliday(dateYmd: string): boolean {
  const dow = new Date(ymdToUtcMs(dateYmd)).getUTCDay();
  return dow === 0 || dow === 6;
}

/** 枠の日付に適用するリードタイム (受付締切の何日前)。土日は holiday、平日は weekday。 */
export function briefingSlotLeadDays(
  dateYmd: string,
  settings: LeadDaysSettings,
): number {
  const lead = isBriefingSlotHoliday(dateYmd)
    ? settings.holidayLeadDays
    : settings.weekdayLeadDays;
  return Number.isFinite(lead) && lead >= 0 ? Math.floor(lead) : 0;
}

/** todayYmd から dateYmd までの日数 (dateYmd - todayYmd)。同日=0、未来=正、過去=負。 */
export function daysUntilYmd(todayYmd: string, dateYmd: string): number {
  return Math.round((ymdToUtcMs(dateYmd) - ymdToUtcMs(todayYmd)) / 86_400_000);
}

/**
 * 枠がまだ予約受付中か (= 受付締切に達していないか) を返す。
 * 受付中の条件: 枠日付までの残り日数 >= リードタイム。
 *   - leadDays=0: 当日 (残り0) まで受付。翌日以降の過去日は締切。
 *   - leadDays=2: 枠の2日前が最終受付。1日前・当日・過去は締切。
 */
export function isBriefingSlotOpen(
  dateYmd: string,
  todayYmd: string,
  settings: LeadDaysSettings,
): boolean {
  return daysUntilYmd(todayYmd, dateYmd) >= briefingSlotLeadDays(dateYmd, settings);
}
